'use client';

import React from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const relatedServices = [
  { label: 'Less Container Load (LCL)', href: '/lcl-transport' },
  { label: 'Airfreight Transport', href: '/airfreight-transport' },
  { label: 'Inland Transport', href: '/inland-transport' },
  { label: 'Customs Representation', href: '/customs-representation' },
  { label: 'Port Operations', href: '/port-operations' },
  { label: 'Cargo Insurance', href: '/cargo-insurance' },
  { label: 'Bank Guarantee', href: '/bank-guarantee' },
];

export default function ContainerTransportPage() {
  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#F4F6F9' }}>
      <Header />
      <main className="flex-1 pt-32 pb-20">
        {/* Hero section */}
        <section className="max-w-6xl mx-auto px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Illustration */}
            <div className="flex items-center justify-center">
              <svg viewBox="0 0 320 280" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-sm lg:max-w-md" aria-label="Container ship illustration">
                <path d="M60 180 L260 180 L240 220 Q200 240 160 240 Q120 240 80 220 Z" stroke="#1B6B8A" strokeWidth="4" fill="none" strokeLinejoin="round"/>
                <rect x="80" y="130" width="160" height="52" rx="4" stroke="#1B6B8A" strokeWidth="4" fill="none"/>
                <circle cx="120" cy="156" r="14" stroke="#1B6B8A" strokeWidth="4" fill="none"/>
                <circle cx="200" cy="156" r="14" stroke="#1B6B8A" strokeWidth="4" fill="none"/>
                <line x1="160" y1="130" x2="160" y2="182" stroke="#1B6B8A" strokeWidth="3"/>
                <rect x="120" y="72" width="80" height="60" rx="4" stroke="#1B6B8A" strokeWidth="4" fill="none"/>
                <path d="M148 72 L148 52 L172 52 L172 72" stroke="#1B6B8A" strokeWidth="4" fill="none" strokeLinejoin="round"/>
                <path d="M155 62 L165 62" stroke="#1B6B8A" strokeWidth="3" strokeLinecap="round"/>
                <line x1="120" y1="92" x2="200" y2="92" stroke="#1B6B8A" strokeWidth="2.5" strokeDasharray="6 4"/>
                <line x1="120" y1="112" x2="200" y2="112" stroke="#1B6B8A" strokeWidth="2.5" strokeDasharray="6 4"/>
                <path d="M40 248 Q70 238 100 248 Q130 258 160 248 Q190 238 220 248 Q250 258 280 248" stroke="#2A8FAB" strokeWidth="3" fill="none" strokeLinecap="round"/>
                <path d="M20 264 Q55 254 90 264 Q125 274 160 264 Q195 254 230 264 Q265 274 300 264" stroke="#7EC8DC" strokeWidth="2.5" fill="none" strokeLinecap="round"/>
              </svg>
            </div>

            {/* Text */}
            <div>
              <div className="w-10 h-1 mb-5 rounded" style={{ background: '#1B6B8A' }} />
              <h1 className="text-4xl lg:text-5xl font-bold mb-6 leading-tight" style={{ color: '#0F2340', fontFamily: 'var(--font-display, sans-serif)' }}>
                Container transport
              </h1>
              <div className="space-y-5 text-base lg:text-lg leading-relaxed" style={{ color: '#3A5070' }}>
                <p>
                  As a leading sea cargo transport company, World Transport Overseas offers tailored shipping solutions to meet diverse business needs. <strong style={{ color: '#0F2340' }}>Full Container Load (FCL)</strong> shipping is the ideal solution for businesses that require the exclusive use of a container for their sea freight needs. Offering increased security and cost-effectiveness for larger shipments, FCL ensures that your cargo remains intact and undisturbed throughout its journey.
                </p>
                <p>
                  World Transport Overseas is dedicated to providing an unparalleled FCL shipping experience that empowers our valued customers with a competitive edge in the market. We take pride in our strategic partnerships with top-tier shipping lines, guaranteeing space allocations and ensuring a wide array of weekly sailings to cater to your needs.
                </p>
                <p>
                  Our extensive network of own subsidiaries and agents spans across the globe, connecting you to every major port with the perfect balance of speed and reliability. With weekly sailings and diverse service offerings, we make it our mission to deliver optimal transit times that keep your business moving forward.
                </p>
              </div>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link
                  href="/transport-quote"
                  className="inline-flex items-center gap-2 px-7 py-3 rounded-full text-white font-semibold text-sm transition-all duration-200 hover:opacity-90 shadow-md"
                  style={{ background: '#1A5FAB' }}
                >
                  Get a Quote
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M5 12h14M13 5l7 7-7 7" /></svg>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Key highlights */}
        <section className="py-14" style={{ background: '#0E1F38' }}>
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="w-10 h-1 mb-4 rounded" style={{ background: '#1B6B8A' }} />
            <h2 className="text-2xl lg:text-3xl font-bold mb-10" style={{ color: '#FFFFFF' }}>Why choose WTO Container Transport?</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { title: 'Weekly Sailings', desc: 'Diverse schedules to every major port worldwide' },
                { title: 'Global Network', desc: 'Own subsidiaries and agents connecting every major port' },
                { title: 'Top-Tier Partners', desc: 'Strategic alliances with leading global shipping lines' },
                { title: 'Space Guarantees', desc: 'Guaranteed allocations so your cargo always moves on schedule' },
              ]?.map((h, i) => (
                <div key={i} className="rounded-2xl p-6" style={{ background: '#132848', border: '1px solid rgba(255,255,255,0.1)' }}>
                  <div className="w-8 h-8 rounded-lg mb-4 flex items-center justify-center" style={{ background: 'rgba(126,200,220,0.2)' }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="#1B6B8A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                      <path d="M9 12l2 2 4-4M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9c1.66 0 3.21.45 4.55 1.23" />
                    </svg>
                  </div>
                  <p className="font-bold text-base mb-1" style={{ color: '#FFFFFF' }}>{h?.title}</p>
                  <p className="text-sm leading-relaxed" style={{ color: '#B8CDE0' }}>{h?.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Container types */}
        <section className="py-14" style={{ background: '#0A1628' }}>
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="w-10 h-1 mb-4 rounded" style={{ background: '#1B6B8A' }} />
            <h2 className="text-2xl lg:text-3xl font-bold mb-10" style={{ color: '#FFFFFF' }}>Container Types</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { name: 'Standard Container', desc: '20ft & 40ft dry containers for general cargo — the most widely used option for non-perishable goods.' },
                { name: 'Open Top Container', desc: 'Removable roof for oversized or top-loaded cargo such as machinery, timber, and heavy equipment.' },
                { name: 'Refrigerated Container', desc: 'Temperature-controlled reefer units for perishable goods, pharmaceuticals, and sensitive cargo.' },
                { name: 'Flat Rack Container', desc: 'Collapsible sides for wide or heavy loads — ideal for vehicles, construction equipment, and pipes.' },
                { name: 'Platform Container', desc: 'No walls or roof — maximum flexibility for extremely oversized or irregularly shaped cargo.' },
              ]?.map((c, i) => (
                <div key={i} className="rounded-2xl p-6" style={{ background: '#132848', border: '1px solid rgba(255,255,255,0.1)' }}>
                  <p className="font-bold text-base mb-2" style={{ color: '#FFFFFF' }}>{c?.name}</p>
                  <p className="text-sm leading-relaxed" style={{ color: '#B8CDE0' }}>{c?.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Related services */}
        <section className="py-14" style={{ background: '#0E1F38' }}>
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="w-10 h-1 mb-4 rounded" style={{ background: '#1B6B8A' }} />
            <h2 className="text-2xl lg:text-3xl font-bold mb-8" style={{ color: '#FFFFFF' }}>Other Services</h2>
            <div className="flex flex-wrap gap-3">
              {relatedServices?.map((s) => (
                <Link
                  key={s?.href}
                  href={s?.href}
                  className="px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 hover:opacity-80"
                  style={{ background: '#132848', color: '#B8CDE0', border: '1px solid rgba(255,255,255,0.15)' }}
                >
                  {s?.label}
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
